import {CustomChartConfiguration} from "../../../../config/content-configuration";
import * as d3 from "d3";

interface SeriesDataElement { key: string; value: number; x: number; label: string }
interface Margins { top: number; right: number; bottom: number; left: number }

interface PopupItemChartProps {
    config:  CustomChartConfiguration;
    view: __esri.MapView | __esri.SceneView | undefined;
    graphic: __esri.Graphic;
}

export class PopupPanelItemChart {
    watchHandles: any[] = [];
    props: PopupItemChartProps;
    data: SeriesDataElement[];
    colors: string[] = [];
    dataPointsVisible=true;
    graphDiv: HTMLElement;
    width = 392;
    height = 230;
    margins: Margins = {top: 35, right: 20, bottom: 30, left: 30 };

    constructor(props: PopupItemChartProps) {
        this.props = props;
        const {config} = this.props;
        if(config.axis.x?.labels && typeof config.axis.x?.labels.entries !== "function") {
            // fix für Proleme mit Map im typescript-json-serializer
            const labels: Map<number, string> = new Map();
            for (const [key, value] of Object.entries(config.axis.x.labels)) {
                labels.set(key as number, value);
            }
            config.axis.x.labels = labels;
        }
        this.callout = this.callout.bind(this);
        this.componentDidMount();
    }
    /**
     * Data extent for Y values
     */
    get xExtent(): Array<number | { valueOf(): number }> {
        const {config} = this.props;
        const data = this.data;
        const ext = d3.extent(data, (d) => d.x);
        if(typeof config.axis?.x?.min !== "undefined") {
            ext[0] = config.axis.x.min;
        }
        if(typeof config.axis?.x?.max !== "undefined") {
            ext[1] = config.axis.x.max;
        }
        // @ts-ignore
        return ext;
    }
    /**
     * Data extent for Y values
     */
    get yExtent(): Array<number | { valueOf(): number }> {
        const {config} = this.props;
        const data = this.data;
        const ext = d3.extent(data, (d) => d.value);
        if(typeof config.axis?.y?.min !== "undefined") {
            ext[0] = config.axis.y.min;
        }
        if(typeof config.axis?.y?.max !== "undefined") {
            ext[1] = config.axis.y.max;
        }
        // @ts-ignore
        return ext;
    }
    componentDidMount(): void {

    }
    componentWillUnmount(): void {
        this.watchHandles.forEach((handle: any) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
    }
    draw(data: SeriesDataElement[]): void {
        const {config} = this.props;
        const node = this.graphDiv;
        // Specify the chart’s dimensions.
        const margins = this.margins;
        // Create the positional scales.
        const x = this.x();
        const y = this.y();

        d3.select(node).select("svg").remove(); // remove previous graphs
        const svg = d3.select(node).append("svg")
            .attr("width", this.width)
            .attr("height", this.height)
            .attr("viewBox", [0, 0, this.width, this.height])
            .attr("style", "max-width: 100%; this.height: auto; overflow: visible; font-size: 10px;");

        // Add the horizontal axis.
        if(config.axis.x?.labels){
            svg.append("g")
                .attr("transform", `translate(0,${this.height - margins.bottom})`)
                .call(d3.axisBottom(x)
                    .tickValues(config.axis.x.labels.keys())
                    .tickSizeOuter(0)
                    .tickFormat((v) =>
                        config.axis.x?.labels?.get(v as number)));
        }else{
            svg.append("g")
                .attr("transform", `translate(0,${this.height - margins.bottom})`)
                .call(d3.axisBottom(x).ticks(this.width / 80).tickSizeOuter(0));
        }

        // Add the vertical axis.
        svg.append("g")
            .attr("transform", `translate(${margins.left},0)`)
            .call(d3.axisLeft(y))
            .call(g => g.select(".domain").remove())
            .call(g => g.selectAll(".tick line").clone()
                .attr("x2", this.width - margins.left - margins.right)
                .attr("stroke-opacity", 0.1))
            .call(g => g.append("text")
                .attr("x", -margins.left)
                .attr("y", 25)
                .attr("fill", "currentColor")
                .attr("text-anchor", "start")
                .text(config.title));


        // Compute the points in pixel space as [x, y, z], where z is the name of the series.
        const points = data.map((d) => [x(d.x), y(d.value), d.key]);

        // Add a container for each series.
        const serie = svg.append("g")
            .selectAll()
            .data(d3.group(data, (d) => d.key))
            .join("g");
        // Draw the lines.
        const path = serie.append("path")
            .attr("fill", "none")
            .attr("stroke", (d) => this.color(d[0]))
            .attr("stroke-width", 1.5)
            .attr("d", (d) => d3.line()
                .x((sd) => x((sd as SeriesDataElement).x))
                .y((sd) => y((sd as SeriesDataElement).value))(d[1]));
        let dataPoints;
        if (this.dataPointsVisible) {
            dataPoints = svg.selectAll("circle").data(data).enter().append("circle")
                .attr("cx", (d) =>this.x()(d.x))
                .attr("cy", (d) => this.y()(d.value))
                .style("fill", (d) => this.color(d.key))
                .attr("r", 3)
                .attr("class", "data-point");
        }
        // append legends
        const legend = svg
            .selectAll("g.legend")
            .data(d3.group(data, (d) => d.key))
            .enter()
            .append("g")
            .attr("class", "legend");
        legend.append("circle")
            .attr("cx", (d, i) => this.width/config.mediaInfos.length*i)
            .attr("cy", 5)
            .attr("r", 3)
            .style("fill", (d) => this.color(d[0]));

        legend.append("text")
            .attr("x", (d, i) => this.width/config.mediaInfos.length*i+8)
            .attr("y", 8)
            .text((d, i) => config.mediaInfos[i].altText? config.mediaInfos[i].altText: d[0] as string );
        // Add an invisible layer for the interactive tip.
        const tooltip = svg.append("g");
        const dot = svg.append("g")
            .attr("display", "none");

        dot.append("circle")
            .attr("r", 5);

        dot.append("text")
            .attr("text-anchor", "middle")
            .attr("y", -8);

        svg
            .on("pointerenter", ()=> {
                path.style("mix-blend-mode", null).style("opacity", 0.15);
                if(dataPoints){
                    dataPoints.style("mix-blend-mode", null).style("opacity", 0.15);
                }
                dot.attr("display", null);
            })
            .on("pointermove", (event)=>{
                const [xm, ym] = d3.pointer(event);
                const i = d3.leastIndex(points, ([x, y]) => Math.hypot(x - xm, y - ym));
                const [x, y, k] = points[i];
                path.style("opacity", (z) => z[0] === k ? null : 0.15).filter((z) => z[0] === k).raise();
                if(dataPoints){
                    dataPoints.style("opacity", (z) => z.key === k ? null : 0.15).filter((z) => z.key === k).raise();
                }
                let label = config.tooltip?.content? config.tooltip.content: "[x-value] [unit]\n[group]";
                if(config.tooltip?.specialContent && config.tooltip.specialContent[i]){
                    label = config.tooltip.specialContent[i];
                }
                const yValueFormatted =
                    data[i].value.toLocaleString("de-CH", { style: "decimal", maximumFractionDigits: 3});
                const xValueFormatted =
                    data[i].x.toLocaleString("de-CH", { style: "decimal", maximumFractionDigits: 3});
                label = label.replaceAll("[y-value]", yValueFormatted);
                label = label.replaceAll("[x-value]", xValueFormatted);
                label = label.replaceAll("[unit]", config.title);
                label = label.replaceAll("[group]", k as string);
                dot.attr("transform", `translate(${x},${y})`);
                dot.style("fill", this.color(k)).raise();
                tooltip
                    .attr("class", "chart-tooltip")
                    .attr("transform", `translate(${x},${y + 10})`)
                    .call(this.callout, label,[x, y]).raise();
            })
            .on("pointerleave", () => {
                path.style("mix-blend-mode", "multiply").style("opacity", null);
                dot.attr("display", "none");
                if(dataPoints){
                    dataPoints.style("mix-blend-mode", null).style("opacity", null);
                }
                tooltip.call(this.callout, null, null);
            })
            .on("touchstart", (event) => event.preventDefault());

    }
    callout(g: any, value: any, pos: any) {
        if (!value) {
            return g.style("display", "none");
        }

        g.style("display", null)
            .style("pointer-events", "none")
            .style("font-size", "10px");

        const path = g.selectAll("path")
            .data([null])
            .join("path");

        const text = g.selectAll("text")
            .data([null])
            .join("text")
            .call((txt: any) =>
                txt.selectAll("tspan")
                    .data((value + "").split(/\n/))
                    .join("tspan")
                    .attr("x", 0)
                    .attr("y", (d: any, i: any) => `${i * 1.1}em`)
                    .style("font-weight", (_: any, i: any) => i ? null : "bold")
                    .text((d: any) => d));

        const {y, width: w, height: h} = text.node().getBBox();
        // Tooltip innerhalb des SVGs
        const left = (pos[0]-w/2 < 0)? -20: (pos[0]+w/2 > this.width)? -(w-(this.width-pos[0])):-w/2;
        if(pos[1]+h+15+20 > this.height){
            text.attr("transform", `translate(${left},${y - 15 - h})`);
            path.attr("d", `M${left - 10},-23H-5l5,5l5,-5H${w + left + 10}v-${h + 20}h-${w + 20}z`);
        }else{
            text.attr("transform", `translate(${left},${15 - y})`);
            path.attr("d", `M${left - 10},5H-5l5,-5l5,5H${w + left + 10}v${h + 20}h-${w + 20}z`);
        }
    }
    /**
     * X value scaling
     */
    x() {
        const {left, right} = this.margins;
        return d3.scaleLinear()
            .domain(this.xExtent)
            .range([
                left,
                this.width - right
            ]);
    }

    /**
     * Y value scaling
     */
    y() {
        const margins = this.margins;
        return d3.scaleLinear()
            .domain(this.yExtent).nice()
            .range([
                this.height - margins.bottom,
                margins.top
            ]);
    }
    color(key: any) {
        //set color pallete for different vairables
        const seriesName = [...new Set(this.data.map((d) => d.key))];
        return this.colors[seriesName.indexOf(key)];
    }
    prepareValues(): SeriesDataElement[] {
        const {config, graphic} = this.props;
        const data: SeriesDataElement[] = [];
        config.mediaInfos.forEach((m: __esri.LineChartMediaInfo)=>{
            this.colors.push("rgba("+m.value.colors[0].join(",")+")");
            const entries = config.axis.x?.labels?.entries();
            m.value.fields.forEach((f: string)=> {
                if(entries) {
                    const next = entries.next();
                    data.push({
                        key: m.title,
                        value: parseFloat(graphic.getAttribute(f)),
                        x: next.value[0],
                        label: next.value[1]
                    });
                }
            });
        });
        this.data = data;
        return data;
    }
    render(): HTMLElement {
        this.graphDiv = document.createElement("div");
        this.graphDiv.className = "geo-chart";
        this.draw(this.prepareValues());
        return this.graphDiv;
    }
}
